import { useCallback } from "react";
import store, { useAppDispatch } from "../app/store";
import { getMateriasAlumno } from "../Api/subject";
import {
  clearPanelSubjectsDataList,
  setFilterTagsList,
  setOfferedSubjects,
} from "../reduxSlices/subjectsSlice";
import {
  setLastUpdateOferta,
  setOfertaInvalidandoOVenciendo,
} from "../reduxSlices/globalFlagsSlice";
import { getCuposAlumnoFijadosThunk, getCuposAlumnos, OfertaParaCupos } from "../reduxAsyncThunks/subjects.thunks";

/**
 * Función auxiliar para obtener tags únicos de las materias
 */
function getUniqueTags(subjects: any) {
  const tagSet = new Set();

  subjects.forEach((subject: any) => {
    subject.sections.forEach((section: any) => {
      if (section.tag) {
        const list = section.tag.split(",");
        list.forEach((element: any) => {
          tagSet.add(element.trim());
        });
      }
    });
  });

  return Array.from(tagSet).sort();
}

/**
 * Cupos a pedir con la oferta que devuelve la consulta:
 * - "completos": fijados + no fijados (carga inicial, oferta regenerada, reconexión y foco)
 * - "fijados": sólo los fijados
 * - "ninguno": la consulta sólo actualiza la oferta
 */
export type ModoCuposTrasOferta = "ninguno" | "completos" | "fijados";

interface FetchMateriasOptions {
  cupos?: ModoCuposTrasOferta;
  /** Refresca las materias fijadas antes de pedir cupos, que se separan según esa lista. */
  actualizarFijadas?: boolean;
}

interface UseMateriasAlumnoOptions {
  /**
   * Si es true, actualiza los tags únicos de las materias
   * @default true
   */
  updateTags?: boolean;
  /**
   * Si es true, actualiza las materias fijadas después de obtener la oferta
   * @default false
   */
  updatePinnedSubjects?: boolean;
  /**
   * Función para actualizar materias fijadas (requerida si updatePinnedSubjects es true)
   */
  refreshPinnedSubjects?: () => Promise<void>;
  /**
   * Si es true, actualiza el timestamp de última actualización de oferta
   * @default true
   */
  updateLastUpdateTimestamp?: boolean;
  /**
   * Flag para controlar si se debe establecer el flag de oferta invalidando/venciendo
   * Solo se usa en la inicialización de App.tsx
   */
  hasSetOfertaFlag?: React.MutableRefObject<boolean>;
}

/**
 * Hook personalizado para obtener y actualizar la oferta de materias del alumno
 * Centraliza la lógica de obtención de materias y actualización de estados Redux
 * 
 * @param studentId - ID del estudiante
 * @param options - Opciones de configuración del hook
 * @returns Función para obtener y actualizar las materias
 */
export const useMateriasAlumno = (
  studentId: number | null,
  options: UseMateriasAlumnoOptions = {}
) => {
  const dispatch = useAppDispatch();
  const {
    updateTags = true,
    updatePinnedSubjects = false,
    refreshPinnedSubjects,
    updateLastUpdateTimestamp = true,
    hasSetOfertaFlag,
  } = options;

  /**
   * Los cupos se calculan sobre la oferta, así que se piden acá, con la respuesta
   * recién recibida. Una oferta invalidando/venciendo va a ser reemplazada: sus
   * cupos no sirven y los pide la consulta que dispara el socket de regeneración.
   */
  const solicitarCuposDeLaOferta = useCallback(
    async (modo: ModoCuposTrasOferta, invalidandoOVenciendo: boolean, oferta: OfertaParaCupos) => {
      if (modo === "ninguno") return;

      if (invalidandoOVenciendo) {
        console.log("-> Cupos: se omiten porque la oferta se está regenerando");
        return;
      }

      if (!studentId) return;

      if (!store.getState().student.debeConsultarCupos) {
        console.log("-> Cupos: se omiten porque el backend indicó debeConsultarCupos=false");
        return;
      }

      if (modo === "fijados") {
        await dispatch(getCuposAlumnoFijadosThunk({ idAlumno: studentId, oferta }));
        return;
      }

      await dispatch(getCuposAlumnos({ idAlumno: studentId, oferta }));
    },
    [dispatch, studentId]
  );

  /**
   * Obtiene las materias del alumno y actualiza los estados de Redux correspondientes
   * 
   * @returns Promise con el resultado de la operación
   */
  const fetchAndUpdateMaterias = useCallback(
    async ({ cupos = "ninguno", actualizarFijadas }: FetchMateriasOptions = {}) => {
      if (!studentId) {
        console.warn("⚠️ useMateriasAlumno: studentId no disponible");
        return { success: false, error: "studentId no disponible" };
      }

      try {

        const oferta = await getMateriasAlumno(studentId.toString());
        const isInvalidandoOVenciendo = oferta.invalidando || oferta.venciendo;

        // Limpiar panel de datos si está invalidando o venciendo
        if (isInvalidandoOVenciendo) {
          dispatch(clearPanelSubjectsDataList());
        }

        if (oferta.status === 200) {
          // Establecer flag de oferta invalidando/venciendo (solo en inicialización)
          if (isInvalidandoOVenciendo && hasSetOfertaFlag && !hasSetOfertaFlag.current) {
            dispatch(setOfertaInvalidandoOVenciendo(true));
            hasSetOfertaFlag.current = true;
          }

          // Una oferta definitiva cierra el loader de regeneración
          if (!isInvalidandoOVenciendo && store.getState().globalFlags.ofertaInvalidandoOVenciendo) {
            dispatch(setOfertaInvalidandoOVenciendo(false));
          }

          // Actualizar tags únicos si está habilitado
          if (updateTags) {
            const uniqueTags = getUniqueTags(oferta.data);
            if (uniqueTags.length > 0) {
              dispatch(setFilterTagsList(uniqueTags));
            }
          }

          // Manejar oferta venciendo con datos vacíos
          if (oferta.venciendo && oferta.data.length === 0) {
            console.log("%cRegenerando oferta...", "color: green; text-decoration: underline;");
          } else {
            // Actualizar oferta de materias
            dispatch(setOfferedSubjects(oferta.data));
          }

          // Actualizar materias fijadas si está habilitado
          if ((actualizarFijadas ?? updatePinnedSubjects) && refreshPinnedSubjects) {
            await refreshPinnedSubjects();
          }

          // Actualizar timestamp de última actualización
          if (updateLastUpdateTimestamp) {
            dispatch(setLastUpdateOferta(new Date().getTime()));
          }

          await solicitarCuposDeLaOferta(cupos, isInvalidandoOVenciendo, oferta.data);

          return {
            success: true,
            data: oferta.data,
            invalidando: oferta.invalidando,
            venciendo: oferta.venciendo,
          };
        } else {
          return {
            success: false,
            error: `Error al obtener materias: status ${oferta.status}`,
          };
        }
      } catch (error: any) {
        console.error("❌ Error en useMateriasAlumno:", error);
        return {
          success: false,
          error: error.message || "Error al obtener materias",
        };
      }
    },
    [
      studentId,
      dispatch,
      updateTags,
      updatePinnedSubjects,
      refreshPinnedSubjects,
      updateLastUpdateTimestamp,
      hasSetOfertaFlag,
      solicitarCuposDeLaOferta,
    ]
  );

  return { fetchAndUpdateMaterias };
};

export default useMateriasAlumno;

